import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CinemaDraw.scss";

// Set to false to disable the egg without deleting files.
// To remove entirely: delete CinemaDraw.js and CinemaDraw.scss, then
// remove the import, useCinemaDraw() hook, title props, overlay render,
// and the pause-while-open lines in ScreeningRoom.js.
export const CINEMA_DRAW_ENABLED = true;

// Activation (secret — no visible control):
//   • Triple-click or triple-tap the H1 “Favorite Cinema.” (including the period)
//   • Or type “draw”, or press Shift+D, while on the page
// Count taps ourselves. Do not use event.detail — browsers reset it, and
// preventDefault on mousedown (to stop text selection) swallows later clicks.

const VISIBLE_CARDS = 16;
const GATHER_MS = 980;
const RIFFLE_MS = 2520;
const CLOSE_MS = 540;
const FLY_MS = 560;
const HOLD_MS = 320;
const FLIP_MS = 680;
const CYCLE_MS = 520;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isCoarsePointer() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

function playableItems(collection) {
  return (collection || []).filter((item) => item && (item.poster || item.still));
}

function shuffleCopy(list) {
  const copy = list.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  return copy;
}

function pickVisible(items, count) {
  return shuffleCopy(items).slice(0, Math.min(count, items.length));
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
}

function mixPose(a, b, t) {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    rx: a.rx + (b.rx - a.rx) * t,
    ry: a.ry + (b.ry - a.ry) * t,
    rz: a.rz + (b.rz - a.rz) * t,
    s: a.s + (b.s - a.s) * t,
    o: a.o + (b.o - a.o) * t,
    zi: t < 0.5 ? a.zi : b.zi,
    blur: (a.blur || 0) + ((b.blur || 0) - (a.blur || 0)) * t,
  };
}

function shelfStart(i, n) {
  return {
    x: (i - (n - 1) / 2) * 58,
    y: 132,
    z: 0,
    rx: 2,
    ry: 0,
    rz: (i - (n - 1) / 2) * 0.45,
    s: 0.7,
    o: 0.88,
    zi: i,
    blur: 0,
  };
}

function pilePose(i, n) {
  const dest = i;
  return {
    x: (dest - (n - 1) / 2) * 0.55 + ((dest * 17) % 5) - 2,
    y: dest * -0.4 + ((dest * 13) % 3) - 1,
    z: dest * 1.15,
    rx: 3,
    ry: 0,
    rz: (dest - (n - 1) / 2) * 0.35,
    s: 1,
    o: 1,
    zi: dest,
    blur: 0,
  };
}

function stackFaceDown(i, n) {
  return {
    x: (i - (n - 1) / 2) * 0.28,
    y: i * 0.35,
    z: -i * 1.1,
    rx: 5,
    ry: 180,
    rz: (i - (n - 1) / 2) * 0.08,
    s: 1,
    o: 1,
    zi: n - i,
    blur: 0,
  };
}

function rifflePose(i, n, t) {
  const half = Math.floor(n / 2);
  const left = i < half;
  const pack = left ? i : i - half;
  const side = left ? -1 : 1;
  const dest = left ? pack * 2 : pack * 2 + 1;

  const split = {
    x: side * (76 + pack * 3.2),
    y: pack * -1.4,
    z: pack * 0.9,
    rx: 9,
    ry: side * 16,
    rz: side * (9 + pack * 0.4),
    s: 1,
    o: 1,
    zi: pack,
    blur: 0,
  };

  const stacked = {
    x: (dest - (n - 1) / 2) * 0.4,
    y: dest * -0.42,
    z: dest * 1.15,
    rx: 4,
    ry: side * 3,
    rz: (dest - (n - 1) / 2) * 0.18,
    s: 1,
    o: 1,
    zi: dest,
    blur: 0,
  };

  if (t < 0.15) {
    return mixPose(pilePose(i, n), split, easeOutCubic(t / 0.15));
  }

  if (t < 0.66) {
    const u = (t - 0.15) / 0.51;
    const release = dest / (n + 1.2);
    const local = (u - release) / 0.2;
    if (local <= 0) return split;
    if (local >= 1) return stacked;
    const k = easeOutCubic(local);
    const lift = Math.sin(k * Math.PI);
    return {
      x: split.x + (stacked.x - split.x) * k,
      y: split.y + (stacked.y - split.y) * k - lift * 30,
      z: split.z + (stacked.z - split.z) * k + lift * 22,
      rx: 9 + lift * 16,
      ry: split.ry * (1 - k),
      rz: split.rz + (stacked.rz - split.rz) * k + side * lift * 7,
      s: 1,
      o: 1,
      zi: dest,
      blur: 0,
    };
  }

  const v = (t - 0.66) / 0.34;
  if (v < 0.42) {
    const k = v / 0.42;
    const span = n <= 1 ? 0.5 : dest / (n - 1);
    const arch = Math.sin(span * Math.PI) * 50 * Math.sin(k * Math.PI);
    return {
      x: stacked.x + (span - 0.5) * 20 * k,
      y: stacked.y - arch,
      z: stacked.z,
      rx: 4 + 24 * k,
      ry: 0,
      rz: stacked.rz + (span - 0.5) * 12 * k,
      s: 1,
      o: 1,
      zi: dest,
      blur: 0,
    };
  }

  const k = easeOutBack(Math.min(1, (v - 0.42) / 0.58));
  return {
    x: stacked.x * (1 - k),
    y: stacked.y * (1 - k),
    z: dest * 1.1 * (1 - k),
    rx: 28 * (1 - k),
    ry: 0,
    rz: stacked.rz * (1 - k),
    s: 1,
    o: 1,
    zi: dest,
    blur: 0,
  };
}

function cyclePose(i, n, top, slideT) {
  const order = (i - top + n) % n;
  if (order === 0) {
    const tuck = slideT > 0.62 ? (slideT - 0.62) / 0.38 : 0;
    const out = Math.min(1, slideT / 0.62);
    if (tuck <= 0) {
      const k = easeOutCubic(out);
      return {
        x: k * 94,
        y: -Math.sin(out * Math.PI) * 15,
        z: 26 + k * 14,
        rx: 6,
        ry: 180,
        rz: k * 11,
        s: 1,
        o: 1,
        zi: 50,
        blur: 0,
      };
    }
    const k = easeInOutCubic(tuck);
    return {
      x: 94 * (1 - k),
      y: 7 * k,
      z: 40 * (1 - k) - k * 10,
      rx: 6 * (1 - k) + 5 * k,
      ry: 180,
      rz: 11 * (1 - k),
      s: 1,
      o: 1,
      zi: 0,
      blur: 0,
    };
  }
  return {
    x: order * 0.42,
    y: order * 0.38,
    z: -order * 1.12,
    rx: 5,
    ry: 180,
    rz: (order - n / 2) * 0.07,
    s: 1,
    o: 1,
    zi: n - order,
    blur: 0,
  };
}

function applyPose(el, spin, pose) {
  if (!el) return;
  el.style.transform = `translate3d(${pose.x}px, ${pose.y}px, ${pose.z}px) rotateX(${pose.rx}deg) rotateZ(${pose.rz}deg) scale(${pose.s})`;
  el.style.filter = pose.blur ? `blur(${pose.blur}px)` : "none";
  el.style.opacity = String(pose.o);
  el.style.zIndex = String(Math.round(pose.zi));
  if (spin) spin.style.transform = `rotateY(${pose.ry}deg)`;
}

const TAP_WINDOW_MS = 500;
const KEY_WINDOW_MS = 1600;

export function useCinemaDraw() {
  const [open, setOpen] = useState(false);
  const taps = useRef({ n: 0, at: 0 });
  const keys = useRef({ buf: "", at: 0 });
  const countedPointer = useRef(false);

  const close = useCallback(() => setOpen(false), []);

  const registerTap = useCallback(() => {
    if (!CINEMA_DRAW_ENABLED) return;
    const now = Date.now();
    if (now - taps.current.at > TAP_WINDOW_MS) taps.current.n = 0;
    taps.current.at = now;
    taps.current.n += 1;
    if (taps.current.n >= 3) {
      taps.current.n = 0;
      setOpen(true);
    }
  }, []);

  // Count on pointerdown so a swallowed click cannot lose the 2nd/3rd tap.
  const onTitlePointerDown = useCallback(
    (e) => {
      if (!CINEMA_DRAW_ENABLED) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      countedPointer.current = true;
      registerTap();
    },
    [registerTap]
  );

  // Fallback when Pointer Events never fire (older WebKit).
  const onTitleClick = useCallback(() => {
    if (countedPointer.current) {
      countedPointer.current = false;
      return;
    }
    registerTap();
  }, [registerTap]);

  useEffect(() => {
    if (!CINEMA_DRAW_ENABLED) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.repeat || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      const node = e.target;
      if (
        node &&
        node.closest &&
        node.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
      const html = document.documentElement;
      if (html.classList.contains("is-cinema-open")) return;
      if (html.classList.contains("is-cinema-draw-open")) return;

      if (e.shiftKey && (e.key === "D" || e.key === "d")) {
        setOpen(true);
        return;
      }

      if (e.key.length !== 1) return;
      const ch = e.key.toLowerCase();
      if (ch < "a" || ch > "z") return;
      const now = Date.now();
      if (now - keys.current.at > KEY_WINDOW_MS) keys.current.buf = "";
      keys.current.at = now;
      keys.current.buf = (keys.current.buf + ch).slice(-4);
      if (keys.current.buf === "draw") {
        keys.current.buf = "";
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!CINEMA_DRAW_ENABLED) {
    return { open: false, close, titleProps: {} };
  }

  return {
    open,
    close,
    titleProps: {
      onPointerDown: onTitlePointerDown,
      onClick: onTitleClick,
    },
  };
}

function CinemaDraw({ collection, onClose }) {
  const pool = useMemo(() => playableItems(collection), [collection]);
  const reduced = prefersReducedMotion();
  const coarse = isCoarsePointer();
  const [visible, setVisible] = useState(() =>
    pickVisible(pool, VISIBLE_CARDS)
  );
  const [phase, setPhase] = useState(reduced ? "cycle" : "gather");
  const [picked, setPicked] = useState(null);
  const [dealId, setDealId] = useState(0);
  const [entered, setEntered] = useState(false);

  const cardRefs = useRef([]);
  const spinRefs = useRef([]);
  const modeRef = useRef(reduced ? "cycle" : "gather");
  const t0Ref = useRef(0);
  const flyIndexRef = useRef(0);
  const nRef = useRef(visible.length);
  const reducedRef = useRef(reduced);

  nRef.current = visible.length;
  reducedRef.current = reduced;

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("is-cinema-draw-open");
    const prev = document.activeElement;
    if (prev && prev.blur) prev.blur();
    return () => html.classList.remove("is-cinema-draw-open");
  }, []);

  useEffect(() => {
    t0Ref.current = performance.now();
    let raf = 0;

    const tick = (now) => {
      const mode = modeRef.current;
      const n = nRef.current;
      const elapsed = now - t0Ref.current;
      const quiet = reducedRef.current;

      if (mode === "gather" && elapsed >= GATHER_MS) {
        if (quiet) {
          modeRef.current = "cycle";
          setPhase("cycle");
          t0Ref.current = now;
        } else {
          modeRef.current = "riffle";
          setPhase("riffle");
          t0Ref.current = now;
        }
      } else if (mode === "riffle" && elapsed >= RIFFLE_MS) {
        modeRef.current = "close";
        t0Ref.current = now;
      } else if (mode === "close" && elapsed >= CLOSE_MS) {
        modeRef.current = "cycle";
        setPhase("cycle");
        t0Ref.current = now;
      } else if (mode === "draw") {
        const total = quiet ? 420 : FLY_MS + HOLD_MS + FLIP_MS;
        if (elapsed >= total) {
          modeRef.current = "reveal";
          setPhase("reveal");
        }
      }

      const live = modeRef.current;
      const t = now - t0Ref.current;

      for (let i = 0; i < n; i += 1) {
        let pose;
        if (live === "gather") {
          const k = Math.min(1, t / GATHER_MS);
          if (quiet) {
            pose = mixPose(
              { ...stackFaceDown(i, n), o: 0, s: 0.94, ry: 0 },
              { ...stackFaceDown(i, n), ry: 0 },
              easeOutCubic(k)
            );
          } else {
            pose = mixPose(shelfStart(i, n), pilePose(i, n), easeOutCubic(k));
          }
        } else if (live === "riffle") {
          pose = rifflePose(i, n, Math.min(1, t / RIFFLE_MS));
        } else if (live === "close") {
          const k = easeInOutCubic(Math.min(1, t / CLOSE_MS));
          pose = mixPose(
            { ...stackFaceDown(i, n), ry: 0, z: 0, y: 0, x: 0 },
            stackFaceDown(i, n),
            k
          );
        } else if (live === "cycle") {
          if (quiet) {
            pose = stackFaceDown(i, n);
          } else {
            const steps = Math.floor(t / CYCLE_MS);
            const slideT = (t % CYCLE_MS) / CYCLE_MS;
            pose = cyclePose(i, n, steps % n, slideT);
          }
        } else if (live === "draw" || live === "reveal") {
          const fly = flyIndexRef.current;
          const rest = stackFaceDown(i, n);
          const shown = {
            x: 0,
            y: -36,
            z: 90,
            rx: 2,
            ry: 0,
            rz: 0,
            s: 1.46,
            o: 1,
            zi: 80,
            blur: 0,
          };
          const facedown = { ...shown, ry: 180, s: 1.38, y: -28 };
          const recede = {
            ...rest,
            y: rest.y + 18,
            z: rest.z - 70,
            s: 0.9,
            o: 0.62,
            blur: 2.4,
            zi: rest.zi,
          };

          if (i === fly) {
            if (quiet) {
              const k = Math.min(1, t / 420);
              pose = mixPose(
                { ...stackFaceDown(i, n), ry: 0 },
                shown,
                easeOutCubic(k)
              );
            } else if (t < FLY_MS) {
              pose = mixPose(
                cyclePose(i, n, fly, 0),
                facedown,
                easeOutCubic(t / FLY_MS)
              );
            } else if (t < FLY_MS + HOLD_MS) {
              pose = facedown;
            } else if (t < FLY_MS + HOLD_MS + FLIP_MS) {
              const k = easeInOutCubic(
                (t - FLY_MS - HOLD_MS) / FLIP_MS
              );
              pose = mixPose(facedown, shown, k);
            } else {
              pose = shown;
            }
          } else if (quiet) {
            const k = Math.min(1, t / 420);
            pose = mixPose(rest, recede, easeOutCubic(k));
          } else {
            const k = Math.min(1, t / FLY_MS);
            pose = mixPose(rest, recede, easeOutCubic(k));
          }
        } else {
          pose = stackFaceDown(i, n);
        }

        applyPose(cardRefs.current[i], spinRefs.current[i], pose);
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dealId]);

  const drawNow = useCallback(() => {
    const mode = modeRef.current;
    if (mode === "draw" || mode === "reveal") return;
    if (!pool.length) return;

    const pick = pool[Math.floor(Math.random() * pool.length)];
    const src = pick.poster || pick.still;
    if (src) {
      const preload = new Image();
      preload.src = src;
    }
    let fly = 0;
    if (mode === "cycle" && !reducedRef.current && nRef.current) {
      const elapsed = performance.now() - t0Ref.current;
      fly = Math.floor(elapsed / CYCLE_MS) % nRef.current;
    }
    flyIndexRef.current = fly;
    setPicked(pick);
    modeRef.current = "draw";
    setPhase("draw");
    t0Ref.current = performance.now();
  }, [pool]);

  const drawAgain = useCallback(() => {
    const next = pickVisible(pool, VISIBLE_CARDS);
    setVisible(next);
    setPicked(null);
    const start = reducedRef.current ? "cycle" : "gather";
    modeRef.current = start;
    setPhase(start);
    setDealId((n) => n + 1);
  }, [pool]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        e.stopPropagation();
        drawNow();
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, drawNow]);

  if (!pool.length) return null;

  const prompt =
    phase === "cycle"
      ? coarse
        ? "Draw"
        : "Press space to draw."
      : null;

  const overlay = (
    <div
      className={`cinema-draw${entered ? " is-in" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Tonight's draw"
      onClick={onClose}
    >
      <button
        type="button"
        className="cinema-draw-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close draw"
      >
        ×
      </button>

      <div
        className="cinema-draw-stage"
        onClick={(e) => {
          e.stopPropagation();
          if (modeRef.current === "cycle") drawNow();
        }}
      >
        <div className="cinema-draw-table" aria-hidden="true" />
        <div className="cinema-draw-deck">
          {visible.map((item, i) => {
            const face =
              picked && i === flyIndexRef.current
                ? picked.poster || picked.still
                : item.poster || item.still;
            return (
              <div
                key={`${dealId}-${item.title}-${i}`}
                className="cinema-draw-card"
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
              >
                <div
                  className="cinema-draw-card-spin"
                  ref={(el) => {
                    spinRefs.current[i] = el;
                  }}
                >
                  <div className="cinema-draw-face is-front">
                    <span className="cinema-draw-mat">
                      <img src={face} alt="" draggable="false" decoding="async" />
                    </span>
                  </div>
                  <div className="cinema-draw-face is-back" aria-hidden="true">
                    <span className="cinema-draw-monogram">AEH</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {prompt && (
          <p className="cinema-draw-prompt">
            {coarse ? (
              <button
                type="button"
                className="cinema-draw-prompt-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  drawNow();
                }}
              >
                Draw
              </button>
            ) : (
              prompt
            )}
          </p>
        )}

        {phase === "reveal" && picked && (
          <div className="cinema-draw-placard">
            <p className="cinema-draw-kicker">Tonight’s pick.</p>
            <p className="cinema-draw-title">{picked.title}</p>
            <p className="cinema-draw-credit">
              {picked.year}
              {picked.by ? ` · ${picked.by}` : ""}
            </p>
            <button
              type="button"
              className="cinema-draw-again"
              onClick={(e) => {
                e.stopPropagation();
                drawAgain();
              }}
            >
              Draw again
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default CinemaDraw;
